import { Box, Drawer, Modal } from '@mui/material'
import {
  Button,
  IconButton,
  Input,
  InteractiveButton,
  Typography
} from '@evocolabs/orkaui'
import { postFaqFeedback, postFaqQuestion } from '@/src/providers/RemotePoint'
import { useEffect, useState } from 'react'

import ArrowLeft24 from '@evocolabs/icons/lib/ArrowLeft24'
import Close24 from '@evocolabs/icons/lib/Close24'
import CloseIcon from '@mui/icons-material/Close'
import Letter64 from '@evocolabs/icons/lib/Letter64'
import Link from 'next/link'
import Markdown from 'markdown-to-jsx'
import QnA64 from '@evocolabs/icons/lib/QnA64'
import i18n from 'i18next'
import k from '@/src/i18n/keys'
import { showChat } from '@/src/utils/chatBot'
import styles from '@/pages/scss/FAQ.module.scss'
import theme from '@/src/theme'
import validateEmail from '@/src/utils/validateEmail'

const ContactDrawer = ({ setDrawerOpen, drawerOpen }) => {
  const contactChoices = [
    ['Chat now', '#'],
    ['Email us', 'mailto:support@hiorka.com'],
    ['FAQ', '/FAQ']
  ]

  const handleOnClick = (ref) => {
    setDrawerOpen(false)
    if (ref === '#') {
      showChat()
    }
  }
  return (
    <Drawer
      anchor="right"
      onClose={() => setDrawerOpen(false)}
      open={drawerOpen}
      onOpen={() => setDrawerOpen(true)}
      transitionDuration={400}
      classes={{ paper: styles['drawer-paper'] }}
    >
      <div className={styles['contact-wrapper']}>
        <CloseIcon
          className={styles['close-icon']}
          onClick={() => setDrawerOpen(false)}
        />

        <div>
          {contactChoices.map((value, index) => (
            <div
              key={index}
              className={styles['contact-header-wrapper']}
              id={`${i18n.t(k.HOVER1)}${index}`}
            >
              <div
                className={styles['contact-header-wrapper-inner']}
                style={{ animationDuration: `${200 + index * 50}ms` }}
              >
                <a
                  href={value[1]}
                  className={styles['contact-header-text']}
                  onClick={() => handleOnClick(value[1])}
                >
                  {value[0]}
                </a>
                <picture>
                  <source
                    srcset="/imgs/arrow_left_black.png"
                    type="image/webp"
                  />

                  <source
                    srcset="/imgs/arrow_left_black.png"
                    type="image/png"
                  />

                  <img
                    src="/imgs/arrow_left_black.png"
                    alt="contact arrow"
                    className={styles['arrow-icon']}
                  />
                </picture>
              </div>
            </div>
          ))}
        </div>
        <div className={styles['contact-footer-wrapper']}>
          <div
            className={styles['contact-footer-phone-email']}
            style={{ animationDuration: '600ms' }}
          >
            <div>
              {i18n.t(k.EMAIL2)}&nbsp;
              <div>{i18n.t(k.CA_SUPPORT_HIORKA_COM)}</div>
            </div>
          </div>
          <div
            className={styles['contact-footer-phone-email']}
            style={{ marginTop: '4px', animationDuration: '800ms' }}
          >
            <div>
              {i18n.t(k.PHONE)}&nbsp;<div>{i18n.t(k._52)}</div>
            </div>
          </div>
          <div
            className={styles['addresss-text']}
            style={{ animationDuration: '1000ms' }}
          >
            {i18n.t(k.ORKA_LABS_INC)} <br />
            {i18n.t(k.S_WACKER_DRIVE_SUITE)} <br />
            {i18n.t(k.CHICAGO_IL)}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

const ChatTag = (props) => {
  return (
    <a href="#!" onClick={showChat}>
      {props.customText ? props.customText : 'chat with us online'}
    </a>
  )
}

const BookCallTag = () => {
  return (
    <a
      style={{ cursor: 'pointer' }}
      href="#!"
      onClick={scheduleRef.current.openSchedule}
    >
      {props.customText ? props.customText : 'book a call'}
    </a>
  )
}

const ContactUs = () => {
  return (
    <a
      style={{ cursor: 'pointer' }}
      onClick={() => {
        document.getElementById('header_contact').click()
      }}
    >
      contact us
    </a>
  )
}
const LeaveUsMessage = () => {
  return (
    <a
      style={{ cursor: 'pointer' }}
      onClick={() => {
        document.getElementById('header_contact').click()
      }}
    >
      leave us a message
    </a>
  )
}

const fAQComponentOverrides = [
  {
    text: 'chat with us online',
    component: ChatTag
  },
  {
    text: 'book a call',
    component: BookCallTag
  },
  {
    text: 'contact us',
    component: ContactUs
  },
  {
    text: 'leave us a message',
    component: LeaveUsMessage
  }
]

function structuralFaqData(data) {
  const structuralData = {}
  const results = data.results.data
  structuralData.sections = data.results.sections

  for (let i = 0; i < data.count; i++) {
    const section = results[i].section
    const question = results[i].question
    const content = results[i].content
    const frequency = results[i].frequency
    const id = results[i].id
    if (!structuralData[section]) {
      if (!structuralData.sections) {
        structuralData.sections = []
      }
      if (structuralData.sections.indexOf(section) === -1) {
        structuralData.sections.push(section)
      }
      structuralData[section] = []
    }
    structuralData[section].push({
      id,
      question,
      content,
      frequency
    })
  }
  for (const key of Object.keys(structuralData)) {
    structuralData[key] = structuralData[key].sort(
      (a, b) => b.frequency - a.frequency
    )
  }
  return structuralData
}

const FAQSubHeader = ({ backUrl, title }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  return (
    <Box className={styles.subHeaderWrapper} mb={3}>
      <div className={styles.subHeaderIcon}>
        {backUrl === 'GO_BACK' ? (
          <Box
            onClick={() => {
              window.history.back()
            }}
          >
            <ArrowLeft24 />
          </Box>
        ) : (
          <Link href={backUrl}>
            <ArrowLeft24 />
          </Link>
        )}
      </div>
      <Typography
        variant="subh1"
        className={styles.subHeaderTitle}
        sx={{ margin: 'auto' }}
      >
        {title}
      </Typography>
      <ContactDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
      <button
        onClick={() => setDrawerOpen(true)}
        id="header_contact"
        style={{ display: 'none' }}
      />
    </Box>
  )
}

const MoreQuestions = ({
  section_name,
  setSucModalOpen = false,
  setUpperModalOpen = false
}) => {
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  useEffect(() => {
    if (validateEmail(window.localStorage.getItem('userID'))) {
      document.getElementById('more_question_email').value =
        window.localStorage.getItem('userID')
    }
  }, [])

  const resetForm = () => {
    setModalOpen(true)
    document.getElementById('more_question_textarea').value = ''
    document.getElementById('more_question_email').value = ''
  }

  const handleSubmit = async () => {
    const data = {
      question: document.getElementById('more_question_textarea').value,
      email: document.getElementById('more_question_email').value,
      section: section_name
    }
    // validation
    setError(null)
    let tempError = null
    if (data.question === '') {
      tempError = { ...tempError, question: 'This field is required' }
    }
    if (data.email === '') {
      tempError = { ...tempError, email: 'This field is required' }
    } else if (!validateEmail(data.email)) {
      tempError = { ...tempError, email: 'Please enter a valid email address' }
    }
    // submit
    if (tempError) {
      setError(tempError)
      return false
    } else {
      await postFaqQuestion(data, resetForm)
    }
    setSucModalOpen && setSucModalOpen(true)
    setUpperModalOpen && setUpperModalOpen(false)
  }
  return (
    <Box className={styles.moreQuestionsWrapper}>
      <Typography variant="h4" mt={5}>
        Still have questions?
      </Typography>
      <Input
        placeholder={'Please leave your feedbacks here...'}
        multiline
        fullWidth
        id="more_question_textarea"
        errormsg={error?.question}
      />
      <Input
        type="email"
        placeholder="Email"
        inputProps={{ maxLength: 50 }}
        fullWidth
        text={`This will be the address to receive follow-up emails.`}
        errormsg={error?.email}
        id="more_question_email"
      />
      <Button
        size="large"
        loadable
        handleClick={handleSubmit}
        id="button_faq-submit"
      >
        Submit
      </Button>
      {!setSucModalOpen && (
        <SuccessModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
      )}
    </Box>
  )
}

const SuccessModal = ({ modalOpen, setModalOpen }) => {
  return (
    <Modal open={modalOpen}>
      <Box className={styles.sectionQstSubmittedWrapperOutter}>
        <Box className={styles.sectionQstSubmittedWrapper}>
          <Box className={styles.sectionQstSubmittedCloseButton}>
            <IconButton onClick={() => setModalOpen(false)}>
              <Close24 />
            </IconButton>
          </Box>
          <Letter64 />
          <Typography variant="h4">Submitted successfully!</Typography>
          <Typography variant="body2" textAlign="center">
            Thank you for your contact! Our service team will reply to your
            inquiry within 2 business days.
          </Typography>
          <Button
            size="large"
            className={styles.ok_button}
            onClick={() => {
              setModalOpen(false)
            }}
            id="button_faq-confirm"
          >
            OK
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

const highlightKeywords = (text, keywords) => {
  if (!Array.isArray(keywords)) {
    console.error('Keywords must be an array')
    return text
  }

  const parts = text.split(new RegExp(`(${keywords.join('|')})`, 'gi'))
  return (
    <Typography variant="T2">
      {parts.map((part, index) =>
        keywords.includes(part.toLowerCase()) ? (
          <Typography
            key={index}
            style={{ color: theme.palette.secondary[500] }}
            variant="T2"
          >
            {part}
          </Typography>
        ) : (
          part
        )
      )}
    </Typography>
  )
}

const FAQEntryWithFeedback = ({ data, pc = true }) => {
  const [feedback, setFeedback] = useState(null)

  const handleFeedback = (like) => {
    setFeedback(like)
    postFaqFeedback(like, data.question)
  }

  return (
    <Box>
      <Markdown
        options={{ overrides: { p: { component: 'div' } } }}
        className={styles.markdownLinkStyle}
      >
        {data.content}
      </Markdown>

      {pc && (
        <Box display="flex" alignItems="center" mt={2}>
          <Box display="flex" alignItems="left" sx={{ width: '250px' }}>
            <Typography variant="Body2" sx={{ width: '250px' }}>
              Was this answer helpful?
            </Typography>
          </Box>
          <Box display="flex" alignItems="left">
            <InteractiveButton
              type="thumb_up"
              isActive={feedback}
              onClick={() => handleFeedback(true)}
              sx={{ width: 32, height: 32, p: '4px 5px' }}
              forceStroke={theme.palette.primary[500]}
              forceFill={feedback ? theme.palette.primary[300] : null}
            />
            <InteractiveButton
              type="thumb_down"
              isActive={feedback === false} // have to be ===false since init state null
              onClick={() => handleFeedback(false)}
              sx={{ width: 32, height: 32, p: '4px 5px', ml: 1 }}
              forceStroke={theme.palette.primary[500]}
              forceFill={feedback === false ? theme.palette.primary[300] : null}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export {
  fAQComponentOverrides,
  structuralFaqData,
  FAQSubHeader,
  MoreQuestions,
  SuccessModal,
  highlightKeywords,
  FAQEntryWithFeedback,
}
